import React, { Fragment } from "react";
import Padder from "components/Padder";
import PricesFields from "components/ShortLinkForm/Fields/Prices";
import FlexPriceFields from "components/ShortLinkForm/Fields/FlexPrice";
import ProductsFields from "components/ShortLinkForm/Fields/Products";
import CouponsFields from "components/ShortLinkForm/Fields/Coupons";
import TaxRatesFields from "components/ShortLinkForm/Fields/TaxRates";
import ReceiverFields from "components/ShortLinkForm/Fields/Receiver";
import AttachmentsFields from "components/ShortLinkForm/Fields/Attachments";
import ShippingFields from "components/ShortLinkForm/Fields/Shipping";
import ConfirmationFields from "components/ShortLinkForm/Fields/Confirmation";
import RedirectsFields from "components/ShortLinkForm/Fields/Redirects";
import TrialFields from "components/ShortLinkForm/Fields/Trial";
import PaymentFields from "components/ShortLinkForm/Fields/Payment";
import PaymentMethodFields from "components/ShortLinkForm/Fields/PaymentMethod";
import { getShortLinkPreviewTitle } from "utils/shortLink";
import { shortLinkCheckoutPreview } from "components/FormerEditor/common/preview";
import FormActions from "components/FormActions";
import RecaptchaNotice from "components/RecaptchaNotice";
import get from "lodash/get";
import compact from "lodash/compact";
import flatten from "lodash/flatten";
import cloneDeep from "lodash/cloneDeep";
import InfoList from "components/InfoList";
import { THEMES, TOOLTIP_POSITION, UI_THEME } from "utils/constants/ui";
import { STATE_KEYS } from "utils/constants/state";
import { PAYMENT_MODE, TEST_MODE_BADGE } from "utils/constants/payment";
import ShortLinkBrowserPreview from "components/ShortLinkBrowserPreview";
import ShortLinkFormTypePicker from "components/ShortLinkFormTypePicker";
import { useFormikContext } from "formik";
import {
  useCouponPromotionCodes,
  useIsAuthed,
  useProductsPrices,
  useTaxRates,
  useShippingRates,
  useCtxReferences
} from "utils/selectors";
import { FORM_KEY } from "utils/constants/form";
import { SHORT_LINK_FORM_TYPE } from "utils/constants/shortLink";
import classnames from "classnames";
import HoverIconButton from "components/HoverIconButton";
import { useScrollWindowTop } from "utils/hooks";
import { COLORS } from "utils/styles";

const REFRESH_RESOURCES = "Refresh resources";
const REFRESHABLE_INTENTS = [
  SHORT_LINK_FORM_TYPE.PRICES,
  SHORT_LINK_FORM_TYPE.PRODUCTS
];

const ShortLinkBuilderContent = ({
  showAuthedFields,
  refreshing,
  refreshResources
}) => {
  const isAuthed = useIsAuthed();
  const {
    values,
    errors,
    resetForm,
    initialValues,
    setValues
  } = useFormikContext();

  useScrollWindowTop();

  const formIntent = values[FORM_KEY].type;
  const primaries = get(values, STATE_KEYS.SHORT_LINK.PRIMARIES, []);
  const mode = get(values, STATE_KEYS.SHORT_LINK.MODE);
  const { products, prices } = useProductsPrices();
  const { coupons } = useCouponPromotionCodes();
  const taxRates = useTaxRates();
  const shippingRates = useShippingRates();
  const { merchantAccount } = useCtxReferences();
  const merchantAccountConfig = get(
    merchantAccount,
    "merchant_account_config",
    {}
  );
  const canRefresh = REFRESHABLE_INTENTS.indexOf(formIntent) > -1;

  const checkoutPreview = shortLinkCheckoutPreview({
    shortLink: values,
    products,
    prices,
    coupons,
    taxRates,
    shippingRates,
    merchantAccountConfig
  });
  if (!isAuthed) {
    checkoutPreview.checkout.badge = TEST_MODE_BADGE;
  }

  const valueSet =
    (primaries && primaries[0] && primaries[0].product && primaries[0].price) ||
    mode === PAYMENT_MODE.SETUP;
  const previewSubtotal = valueSet
    ? getShortLinkPreviewTitle({
        shortLink: values,
        products,
        prices,
        coupons,
        taxRates,
        shippingRates
      })
    : "Build a payment link";

  const messagesList = errors && errors.messages && errors.messages.length > 0 && (
    <div className="pb3-ns w-100 dib">
      <InfoList header="Error" items={errors.messages} theme={THEMES.ERROR} />
    </div>
  );

  let content;
  const activeType = get(values, `${FORM_KEY}.type`);

  if (!activeType) {
    content = (
      <div className="ph3 w-100">
        <ShortLinkFormTypePicker />
      </div>
    );
  } else {
    let formFields = [
      <PaymentFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />,
      <TaxRatesFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />,
      <CouponsFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />,
      showAuthedFields && <AttachmentsFields />,
      <ReceiverFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />,
      <ShippingFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
    ];
    let upgradePrompt;
    if (formIntent === SHORT_LINK_FORM_TYPE.PRICE) {
      const firstFlexPricePath = `${STATE_KEYS.SHORT_LINK.PRICES}[0]`;
      formFields = [
        <div className="w-100 flex items-center justify-center pb3">
          <FlexPriceFields name={firstFlexPricePath} />
        </div>,
        <PaymentFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />,
        <TaxRatesFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />,
        <CouponsFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />,
        showAuthedFields && <AttachmentsFields />,
        <ReceiverFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
      ];
    } else if (formIntent === SHORT_LINK_FORM_TYPE.SETUP) {
      formFields = [
        <PaymentMethodFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} open />,
        <ReceiverFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
      ];
    } else if (formIntent === SHORT_LINK_FORM_TYPE.PRICES) {
      formFields.unshift([
        <PricesFields />,
        <TrialFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
      ]);
    } else if (
      formIntent === SHORT_LINK_FORM_TYPE.PRODUCTS ||
      formIntent === SHORT_LINK_FORM_TYPE.AUTHORIZE
    ) {
      formFields.unshift([
        <ProductsFields />,
        <TrialFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />
      ]);
    }

    if (showAuthedFields) {
      formFields.push(<RedirectsFields />);
      formFields.push(
        <ConfirmationFields name={STATE_KEYS.SHORT_LINK.CONFIRMATION_DATA} />
      );
    }

    formFields.push(
      <div className="dn db-l">
        <Padder theme="visible" />
      </div>
    );
    const preparedFields = compact(flatten(formFields));

    content = (
      <Fragment>
        <div className="flex-l flex-column fl h-100-l overflow-y-scroll-l w-100 pa3 relative pt4 pt3-l bg-white">
          {messagesList && (
            <div className="dib dn-ns pt3 w-100">{messagesList}</div>
          )}
          <ul className="list ma0 pa0 flex flex-column">
            {preparedFields.map((node, nodeIx) => (
              <div className="pt3" key={nodeIx}>
                {node}
              </div>
            ))}
          </ul>
        </div>
        <div className="flex-l flex-column fl h-100-l relative overflow-y-scroll-l bg-white w-100 ph3 pb3 pt0 pt3-l">
          <div className="pt2 pt3-l">
            {messagesList && (
              <div className="dn dib-ns w-100">{messagesList}</div>
            )}
            {upgradePrompt}
            <div className="pb3 black-70">
              <div className="f4 fw5 black-60">Checkout preview</div>
            </div>
            <ShortLinkBrowserPreview
              shortLink={values}
              preview={checkoutPreview}
              hideCode
              hideUrl
            />
            {!isAuthed && (
              <div className="pv3 ph2">
                <RecaptchaNotice />
              </div>
            )}
            <div className="dn db-l">
              <Padder />
            </div>
          </div>
          <Padder />
        </div>
      </Fragment>
    );
  }

  return (
    <div className="flex flex-row bg-white h-100 w-100 overflow-hidden-l mw-app center">
      {activeType && (
        <div className="fixed bg-white z-2 pa3 bb hairline-1 w-100 mw-app center pt3 mt2-l">
          <FormActions
            theme={UI_THEME.SLIM}
            submitCopy="Create"
            reset={() => {
              const initialPartial = cloneDeep(initialValues);
              initialPartial[FORM_KEY].type = values[FORM_KEY].type;
              setValues(initialPartial);
            }}
            customClasses={{
              container:
                "w-100 flex flex-column flex-row-l justify-between items-end items-center-l"
            }}
            childActions={
              canRefresh &&
              refreshResources && (
                <div className="br hairline-1 pr3 mr3">
                  <HoverIconButton
                    type="refresh"
                    tooltip={REFRESH_RESOURCES}
                    tooltipPosition={TOOLTIP_POSITION.LEFT}
                    loading={refreshing}
                    onClick={refreshResources}
                  />
                </div>
              )
            }
          >
            <div className="flex flex-row items-center">
              <div className="pr2">
                <HoverIconButton
                  color={COLORS.blue}
                  type="leftArrow"
                  onClick={resetForm}
                />
              </div>
              <div className="f5 f3-l fw6 self-start pb3 pb0-l lh-title w-100">
                {previewSubtotal}
              </div>
            </div>
          </FormActions>
        </div>
      )}
      <div
        className={classnames(
          "flex flex-column flex-row-l bg-white h-100 w-100 overflow-y-scroll-l overflow-hidden-l mw-app center",
          {
            "pt3 pt5-ns": !activeType,
            "pt5": activeType
          }
        )}
      >
        {content}
      </div>
    </div>
  );
};

export default ShortLinkBuilderContent;
